import React from "react";
import { motion } from "framer-motion";
import { useSiteContent } from "@/siteContent";
import { useTr } from "@/autoTranslate";

const ACCENTS = ["var(--cobalt)", "var(--coral)", "var(--chartreuse)"];

const cardVariant = {
  hidden: { opacity: 0, y: 60 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Projects() {
  const site = useSiteContent();
  const tr = useTr();

  let list = [];
  try {
    list = JSON.parse(site.get("projects.list", "[]"));
  } catch (e) {
    list = [];
  }

  const title = site.get("projects.title");
  const blurb = site.get("projects.blurb");

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative px-6 md:px-10 py-8 md:py-12 bg-[color:var(--ink)] text-[color:var(--bone)]"
    >
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-15%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="display-serif italic text-6xl md:text-[10vw] leading-[0.9]"
      >
        {tr(title)}
      </motion.h2>

      <p className="mt-6 md:mt-8 max-w-xl text-base md:text-lg opacity-80">
        {tr(blurb)}
      </p>

      <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {list.map((p, i) => {
          const accent = ACCENTS[i % ACCENTS.length];
          const photo = p.photo || p.image || "";
          return (
            <motion.article
              key={i}
              variants={cardVariant}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-10%" }}
              whileHover={{ y: -8 }}
              data-cursor="view"
              data-cursor-label="view"
              className="group relative rounded-sm border border-white/15 bg-[color:var(--ink-soft)] p-6 md:p-8 transition-colors"
              data-testid={`project-card-${i}`}
            >
              {/* Cover image / placeholder */}
              <div
                className="relative aspect-[4/3] w-full rounded-sm overflow-hidden"
                style={{ background: accent }}
              >
                {photo ? (
                  <img
                    src={photo}
                    alt={p.title || ""}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="display-serif italic text-6xl md:text-7xl"
                      style={{ color: "var(--ink)" }}
                    >
                      {(p.title || "•").split(" ")[0]}
                    </div>
                  </div>
                )}
              </div>

              <h3 className="mt-6 display-sans text-3xl md:text-4xl leading-tight">
                {tr(p.title || "")}
              </h3>
              <p className="mt-4 text-sm md:text-base leading-relaxed opacity-85 max-w-md">
                {tr(p.blurb || p.description || "")}
              </p>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
}
